import React, {
  useRef,
  useState,
  useEffect,
} from 'react';
import Player from '@vimeo/player';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';
import i18n from 'i18next';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  uiActions,
} from 'web-actions';

import {
  Icon,
  Button,
} from 'web-components';

import {
  Flex,
  Text,
} from 'web-styled';

import * as S from './styled';

const mapState = state => ({
  isPro: coreSelectors.getProfile('is_pro')(state),
});

type VideoState = {
  error?: boolean,
  available?: boolean,
  processing?: boolean,
};

type Props = {
  /* video id */
  id: number | string,
  /* video url */
  src: string,
  title: string,
  height?: string,
  entityId: string,
  readOnly: boolean,
  isRounded: boolean,
  requestType: string,
  resourceType: string,
  buildQuery: { (): string },
  onAssign: { (video: any): void },
  onUploaded: { (videos: any[]): void },
  onDelete: { (): void },
};

const Video = ({
  id,
  src,
  title,
  height,
  readOnly,
  entityId,
  isRounded,
  requestType,
  buildQuery,
  resourceType,
  onAssign,
  onUploaded,
  onDelete,
}: Props) => {
  const ref = useRef<HTMLIFrameElement>(null);
  const iframe = useRef<HTMLIFrameElement>(null);
  const isVimeo = src && src.indexOf('vimeo') !== -1;
  const [videoState, setVideoState] = useState<VideoState>({
    available: !isVimeo,
  });
  const { isPro } = useSelector(mapState, shallowEqual);
  const dispatch = useDispatch();

  const label = i18n.t(`short.label.${resourceType}`);

  useEffect(() => {
    function replaceSrc() {
      setVideoState({
        available: true,
      });
      iframe?.current?.contentWindow?.location.replace(src);
    }

    if (id && !src) {
      setVideoState({
        error: true,
        processing: true,
      });
    } else if (src && isVimeo) {
      const player = new Player(ref.current);
      player.on('loaded', () => {
        console.log('loaded');
        replaceSrc();
      });
      player.on('error', () => {
        console.log('error');
        setVideoState({
          error: false,
          processing: true,
        });
      });
    } else if (src) {
      replaceSrc();
    } else {
      setVideoState({
        error: true,
      });
    }
  }, [id, src]);

  const handleUpdate = (payload, callbacks = {}) => {
    dispatch(
      coreActions.requestUpdateEntity({
        id: entityId,
        payload,
        requestType,
        resourceType,
        callbacks,
      }),
    );
  };

  const handleDeleteVideo = () => {
    const confirmModal: any = {
      modal: {
        title: 'Delete Video?',
        bodyText: 'Are you sure you want to delete this video?',
        confirmText: 'Delete',
      },
    };

    if (onDelete) {
      confirmModal.onConfirm = onDelete;
    } else {
      confirmModal.onConfirmAction = coreActions.requestUpdateEntity({
        id: entityId,
        payload: {
          video_id: null,
          video_url: null,
        },
        requestType,
        resourceType,
      });
    }

    dispatch(
      uiActions.confirmModal(confirmModal),
    );
  };

  const handleUpdateVideo = () => {
    const handleAssign = (v: any) => {
      handleUpdate({
        video_id: v.type === 'youtube' ? null : v.id,
        video_url: v.embed_url,
      });
    };
    const handleUploaded = (videos: any[]) => {
      handleUpdate({
        video_id: videos[0].id,
      });
    };

    const assignedId = isVimeo ? id : (src || '').split('/').pop();
    const query = buildQuery ? buildQuery() : (title || '');
    dispatch(
      uiActions.searchVideoModal({
        query,
        modal: {
          title: `Add ${label} Video`,
          assignedId,
        },
        onAssign: onAssign || handleAssign,
        onUploaded: onUploaded || handleUploaded,
      }),
    );
  };

  return (
    <Flex
      column
      width="100%"
      height="100%"
      noPrint
    >
      {isVimeo && (
        <iframe
          src={src}
          hidden
          ref={ref}
          title="title"
          allow="autoplay; encrypted-media"
        />
      )}

      <S.Video
        style={{
          display: videoState.available ? 'flex' : 'none',
        }}
      >
        <S.Frame
          src="about:blank"
          ref={iframe}
          title={title}
          allowFullScreen
        />
      </S.Video>

      {videoState.error && (
        <S.Placeholder
          height={height}
          isRounded={isRounded}
        >
          <S.PlaceholderIcon>
            <Icon
              type="fas fa-play"
            />
          </S.PlaceholderIcon>
          {videoState.processing ? (
            <Text whiteSpace="pre-line">
              {i18n.t('video.processing')}
            </Text>
          ) : (
            <Text whiteSpace="pre-line">
              {i18n.t('video.not_assign', { type: label })}
              <br />
              {!isPro && 'Contact your Fitness Professional'}
            </Text>
          )}

          {!readOnly && (
            <Button
              // ghost
              appearance="primary"
              onClick={handleUpdateVideo}
            >
              Assign video
            </Button>
          )}
        </S.Placeholder>
      )}

      {(videoState.available && !readOnly) && (
        <S.Controls>
          <Flex
            spacing={5}
            align="center"
          >
            <Icon type={`fab fa-${isVimeo ? 'vimeo-v' : 'youtube'}`} />
            <Text>{isVimeo ? 'Vimeo' : 'Youtube'}</Text>
          </Flex>
          <Flex>
            <Button appearance="link" onClick={handleUpdateVideo}>
              REPLACE VIDEO
            </Button>
            <Button appearance="link" onClick={handleDeleteVideo}>
              <Icon type="far fa-trash-alt" />
            </Button>
          </Flex>
        </S.Controls>
      )}
    </Flex>
  );
};

Video.defaultProps = {
  height: '400px',
};

export default Video;
